import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BriefcaseIcon, HomeIcon, SuccessDocConfirmationIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter'
import { useNavigate } from 'react-router-dom'
import "../../css/hire-create.css"
import MainBreadcrumb from '../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb'
import SelectField from '../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField'
import { CareerStatus, createJobApi, CreateJobBody, DepartmentItem, FieldErrors, getDepartmentIndexApi, getJobLevelIndexApi, getJobTypeIndexApi, JobLevelItem, JobTypeItem } from './hire.type'
import TextField from '../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField'
import CmsEditor from '../../../../../component-controller-mod/component-serv/texteditor-fs-cp/tip-tap-editor/ts/cms-editor'
import { Button, Confirmation } from '@core/uilibrary'
import { useGlobalPopupProvider } from '../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider'
import { useToast } from '../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider'
import { groupPermissionAccessCMSCareer } from '../../../../../context-controller-mod/context-serv/permission-provider/permissionExporter'

type Option = { value: string; label: string };

const toOptions = <T,>(
  items: T[],
  getId: (x: T) => string,
  getLabel: (x: T) => string
): Option[] => {
  return [
    { value: "all", label: "All" },
    ...items.map((x) => ({ value: getId(x), label: getLabel(x) })),
  ];
};

const HireCreate = () => {
  const navigate = useNavigate()
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const inputsSectionRef = useRef<HTMLElement | null>(null);
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();
  const { showToast } = useToast();


  const [status, setStatus] = useState<string>("");

  const [jobCode, setJobCode] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  const [jobLevelOptions, setJobLevelOptions] = useState<Option[]>([]);
  const [jobTypeOptions, setJobTypeOptions] = useState<Option[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<Option[]>([]);

  const [jobLevelUuid, setJobLevelUuid] = useState<string | null>(null);
  const [jobTypeUuid, setJobTypeUuid] = useState<string | null>(null);
  const [departmentUuid, setDepartmentUuid] = useState<string | null>(null);

  // textareas
  const [jobDescription, setJobDescription] = useState("");
  const [jobResponsibleText, setJobResponsibleText] = useState("");
  const [skillRequirementText, setSkillRequirementText] = useState("");
  const [qualificationText, setQualificationText] = useState("");
  const [whatWeOfferText, setWhatWeOfferText] = useState("");

  // editors (separate)
  const [jobResponsibleHtml, setJobResponsibleHtml] = useState("");
  const [skillRequirementHtml, setSkillRequirementHtml] = useState("");
  const [qualificationHtml, setQualificationHtml] = useState("");
  const [whatWeOfferHtml, setWhatWeOfferHtml] = useState("");

  const canCreate = groupPermissionAccessCMSCareer.create()

  useEffect(() => {
    if (!canCreate) {
      navigate("/forbidden")
    }

    let alive = true;

    const run = async () => {
      try {
        const [lvlRes, typeRes, depRes] = await Promise.all([
          getJobLevelIndexApi(),
          getJobTypeIndexApi(),
          getDepartmentIndexApi(),
        ]);

        if (!alive) return;

        const lvlItems: JobLevelItem[] =
          lvlRes?.data?.data?.data?.items ?? [];

        const typeItems: JobTypeItem[] =
          typeRes?.data?.data?.items ?? [];

        const depItems: DepartmentItem[] =
          depRes?.data?.data?.items ?? [];

        setJobLevelOptions(toOptions(lvlItems, (x) => x.uuid, (x) => x.name));
        setJobTypeOptions(toOptions(typeItems, (x) => x.uuid, (x) => x.name));
        setDepartmentOptions(toOptions(depItems, (x) => x.departmentUuid, (x) => x.name));

      } catch (e) {
        console.error("Load dropdown data failed:", e);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, []);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    resize();
  }, [value]);

  const handleSave = () => {
    const nextErrors: FieldErrors = {};

    if (!jobCode.trim()) nextErrors.jobCode = "x";
    if (!jobTitle.trim()) nextErrors.jobTitle = "x";

    if (!jobLevelUuid) nextErrors.jobLevel = "x";
    if (!jobTypeUuid) nextErrors.jobType = "x";
    if (!departmentUuid) nextErrors.department = "x";
    if (!status) nextErrors.status = "x";

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      inputsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const payload: CreateJobBody = {
      jobTitle,
      jobCode,
      status,
      departmentId: departmentUuid!,
      jobLevelId: jobLevelUuid!,
      jobTypeId: jobTypeUuid!,
      description: jobDescription,
      responsibilities: JSON.stringify({
        responsibleDescription: jobResponsibleText,
        responsibleHtml: jobResponsibleHtml,
      }),
      requirements: JSON.stringify({
        requirementsDescription: skillRequirementText,
        requirementsHtml: skillRequirementHtml,
      }),
      qualification: JSON.stringify({
        qualificationDescription: qualificationText,
        qualificationHtml: qualificationHtml,
      }),
      offer: JSON.stringify({
        offerDescription: whatWeOfferText,
        offerHtml: whatWeOfferHtml,
      }),
    };

    openConfirm(
      <Confirmation
        visible
        title="Create job?"
        description="Are you sure you want to create this job?"
        status="info"
        onClose={closeConfirm}
        icon={<SuccessDocConfirmationIcon/>}
        buttons={
          <>
            <Button
              variant="ghost"
              size="md"
              value="Cancel"
              type="button"
              disabled={saving}
              onClick={closeConfirm}
            />

            <Button
              size="md"
              value={saving ? "Saving..." : "Confirm"}
              type="button"
              disabled={saving}
              onClick={async () => {
                try {
                  setSaving(true);

                  const res = await createJobApi(payload);
                  if (res) {
                    showToast({
                      status: "success",
                      title: "Created",
                      description: "Job has been created successfully.",
                    });
                  }

                  closeConfirm();
                  navigate("/career/hire");
                } catch (e) {
                  if (e) {
                    showToast({
                      status: "error",
                      title: "Failed",
                      description: "Job cannot be created.",
                    });
                  }

                } finally {
                  setSaving(false);
                }
              }}
            />
          </>
        }
      />
    );
  };

  return (
    <main className='scl--crr-add-const-styl-main'>
      <section ref={inputsSectionRef} className='scl--crr-add-const-bread-sec-on'>
        <button onClick={() => navigate("/career/hire")}>
          <HomeIcon />
          <span className='span-bread'>Hiring</span>
        </button>
        <span>/</span>
        <h6>
          <BriefcaseIcon />
          <span className='span-bread'>
            Create
          </span>
        </h6>
      </section>

      <section className='scl--crr-add-const-title-sec-tw'>
        <MainBreadcrumb
          title="New application"
          paragraph="Controls how content is created, reviewed, published, and managed to maintain."
        />
      </section>

      <section className='scl--crr-const-inp-sty-box'>
        <div className='grid-item'>
          <TextField
            name="jobcode"
            label="Job code"
            placeholder="Enter job code"
            value={jobCode}
            onChange={(v) => {
              setJobCode(v);
              if (v.trim()) setErrors((p) => ({ ...p, jobCode: undefined }));
            }}
            required
            error={!!errors.jobCode}
          />
        </div>

        <div className='grid-item'>
          <TextField
            name="jobtitle"
            label="Job Title"
            placeholder="Enter job title"
            value={jobTitle}
            onChange={(v) => {
              setJobTitle(v);
              if (v.trim()) setErrors((p) => ({ ...p, jobTitle: undefined }));
            }}
            required
            error={!!errors.jobTitle}
          />
        </div>

        <div className="grid-item">
          <SelectField
            name="jobLevel"
            placeholder="Job level"
            label="Job level"
            options={jobLevelOptions}
            value={jobLevelUuid ?? ""}
            onChange={(v) => {
              const val = v === "all" ? null : String(v);
              setJobLevelUuid(val);
              setErrors((p) => ({ ...p, jobLevel: undefined }));
            }}
            required
            error={!!errors.jobLevel}
          />

        </div>

        <div className="grid-item">
          <SelectField
            name="jobType"
            placeholder="Job type"
            label="Job type"
            options={jobTypeOptions}
            value={jobTypeUuid ?? ""}
            onChange={(v) => {
              const val = v === "all" ? null : String(v);
              setJobTypeUuid(val);
              setErrors((p) => ({ ...p, jobType: undefined }));
            }}
            required
            error={!!errors.jobType}
          />
        </div>

        <div className="grid-item">
          <SelectField
            name="department"
            placeholder="Department"
            label="Department"
            options={departmentOptions}
            value={departmentUuid ?? ""}
            onChange={(v) => {
              const val = v === "all" ? null : String(v);
              setDepartmentUuid(val);
              setErrors((p) => ({ ...p, department: undefined }));
            }}
            required
            error={!!errors.department}
          />
        </div>

        <div className="grid-item">
          <SelectField
            name="status"
            placeholder="status"
            label="status"
            options={CareerStatus}
            value={status}
            onChange={(v) => {
              setStatus(String(v));
              setErrors((p) => ({ ...p, status: undefined }));
            }}
            required
            error={!!errors.status}
          />
        </div>
      </section>

      <section className='scl--crr-deta-jb-add-sty-box'>
        <div className='scl--crr-dja-sty-box-item'>
          <h6>Job Description</h6>
          <textarea
            className="text-area-fst-jb"
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
          />
        </div>

        <div className='scl--crr-dja-sty-box-item'>
          <h6>Job responsible</h6>
          <textarea
            ref={ref}
            className="text-area-ota"
            value={jobResponsibleText}
            onChange={(e) => setJobResponsibleText(e.target.value)}
          />
          <CmsEditor
            value={jobResponsibleHtml}
            onChange={setJobResponsibleHtml}
            minHeight={92}
          />
        </div>

        <div className='scl--crr-dja-sty-box-item'>
          <h6>Skill requirement</h6>
          <textarea
            className="text-area-ota"
            value={skillRequirementText}
            onChange={(e) => setSkillRequirementText(e.target.value)}
          />
          <CmsEditor
            value={skillRequirementHtml}
            onChange={setSkillRequirementHtml}
            minHeight={92}
          />
        </div>

        <div className='scl--crr-dja-sty-box-item'>
          <h6>Qualification</h6>
          <textarea
            className="text-area-ota"
            value={qualificationText}
            onChange={(e) => setQualificationText(e.target.value)}
          />
          <CmsEditor
            value={qualificationHtml}
            onChange={setQualificationHtml}
            minHeight={92}
          />
        </div>

        <div className='scl--crr-dja-sty-box-item'>
          <h6>What we offer</h6>
          <textarea
            className="text-area-ota"
            value={whatWeOfferText}
            onChange={(e) => setWhatWeOfferText(e.target.value)}
          />
          <CmsEditor
            value={whatWeOfferHtml}
            onChange={setWhatWeOfferHtml}
            minHeight={92}
          />
        </div>
      </section>

      <section className='scl--crr-btn-add-sty-box'>
        <Button
          variant="ghost"
          value="Cancel"
          type="button"
          onClick={() => navigate("/career/hire")}
        />
        {canCreate &&
          <Button
            value="Save"
            type="button"
            onClick={handleSave}
            disabled={saving}
          />
        }

      </section>
    </main>
  )
}

export default HireCreate